import { useMemo, useState } from 'react';
import { useGetOpenToolDetails } from '@/modules/gpt-chats/hooks/use-tools';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui-kit/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui-kit/tabs';
import { MarkdownRenderer } from '@/modules/gpt-chats/components/markdown-renderer';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Badge } from '@/components/ui-kit/badge';
import { ScrollArea, ScrollBar } from '@/components/ui-kit/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { getMethodColors } from '@/modules/gpt-chats/constants/tools';
import { FileSearch } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ToolDetailsModalProps {
  toolId: string | null;
  onClose: () => void;
}

interface ApiRow {
  actionId: string;
  method: string;
  name: string;
  endpoint: string;
  inputParameters: string[];
}

const EmptyState = ({ message }: { message: string }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
    <FileSearch className="h-8 w-8 text-muted-foreground/50" />
    <p className="text-sm text-muted-foreground">{message}</p>
  </div>
);

export const ToolDetailsModal = ({ toolId, onClose }: ToolDetailsModalProps) => {
  const { data, isLoading } = useGetOpenToolDetails(toolId ?? '', !!toolId);
  const { t } = useTranslation();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 5,
  });

  const apiRows = useMemo<ApiRow[]>(() => {
    if (data?.Type.startsWith('mcp')) {
      if (!data?.MCPConfig?.Actions) return [];
      return Object.entries(data.MCPConfig.Actions).map(([actionId, action]) => ({
        actionId,
        method: action.Method,
        name: action.Name,
        endpoint: action.Endpoint,
        inputParameters: Object.keys(action.InputParameters || {}),
      }));
    } else {
      if (!data?.APIConfig?.Actions) return [];
      return Object.entries(data.APIConfig.Actions).map(([actionId, action]) => ({
        actionId,
        method: action.Method,
        name: action.Name,
        endpoint: action.Endpoint,
        inputParameters: Object.keys(action.InputParameters || {}),
      }));
    }
  }, [data]);

  const columns = useMemo<ColumnDef<ApiRow>[]>(() => {
    const baseColumns: ColumnDef<ApiRow>[] = [
      {
        accessorKey: 'method',
        header: t('TOOL_METHOD'),
        cell: ({ row }) => {
          const method = (row.getValue('method') as string).toUpperCase();
          const colorClass = getMethodColors(method);
          return (
            <Badge
              variant="secondary"
              className={`pointer-events-none w-fit shrink-0 rounded-full ${colorClass}`}
            >
              {method}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'name',
        header: t('TOOL_NAME_COLUMN'),
        cell: ({ row }) => <span className="font-medium">{row.getValue('name')}</span>,
      },
    ];

    if (!data?.Type?.startsWith('mcp')) {
      baseColumns.push({
        accessorKey: 'endpoint',
        header: t('TOOL_ENDPOINT'),
        cell: ({ row }) => (
          <span className="break-all font-mono text-xs text-muted-foreground">
            {row.getValue('endpoint')}
          </span>
        ),
      });
    }

    baseColumns.push({
      accessorKey: 'inputParameters',
      header: t('TOOL_INPUT_PARAMETERS'),
      cell: ({ row }) => {
        const params = row.getValue('inputParameters') as string[];
        if (!params.length) {
          return <span className="text-xs text-muted-foreground">—</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {params.map((param, i) => (
              <Badge
                key={i}
                variant="secondary"
                className="pointer-events-none rounded-full bg-secondary text-xs text-low-emphasis"
                title={param}
              >
                {param}
              </Badge>
            ))}
          </div>
        );
      },
    });

    return baseColumns;
  }, [data?.Type, t]);

  const table = useReactTable({
    data: apiRows,
    columns,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <Dialog open={!!toolId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[80vh] min-h-[400px] max-w-3xl flex-col overflow-hidden p-0">
        {isLoading ? (
          <div className="space-y-4 p-6">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : data ? (
          <>
            <DialogHeader className="border-b px-6 py-4">
              <DialogTitle className="text-lg font-semibold">{data.Name}</DialogTitle>
            </DialogHeader>

            <Tabs defaultValue="overview" className="flex min-h-0 flex-1 flex-col">
              <div className="border-b px-6">
                <TabsList className="h-auto rounded-none bg-transparent p-0">
                  {(['overview', 'api', 'tutorial'] as const).map((tab) => (
                    <TabsTrigger
                      key={tab}
                      value={tab}
                      className="rounded-none border-b-2 border-transparent px-4 py-3 text-sm capitalize data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                    >
                      {tab === 'tutorial'
                        ? t('TUTORIAL_GUIDE')
                        : tab === 'api'
                          ? t('API')
                          : t('OVERVIEW')}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              {/* Overview Tab */}
              <TabsContent value="overview" className="flex-1 overflow-y-auto">
                <ScrollArea className="h-full">
                  <div className="space-y-5 px-6 py-5">
                    {data.Description ? (
                      <div>
                        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          {t('DESCRIPTION')}
                        </p>
                        <p className="text-sm text-foreground">{data.Description}</p>
                      </div>
                    ) : null}

                    {data.APIConfig?.BaseURL || data.MCPConfig?.ServerURL ? (
                      <div>
                        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          {t('BASE_URL_LABEL')}
                        </p>
                        <p className="break-all font-mono text-sm text-foreground">
                          {data.Type.startsWith('mcp')
                            ? data.MCPConfig?.ServerURL
                            : data.APIConfig?.BaseURL}
                        </p>
                      </div>
                    ) : null}

                    {!data.Description && !data.APIConfig?.BaseURL && (
                      <EmptyState message={t('NO_OVERVIEW_INFO')} />
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>

              {/* API Tab */}
              <TabsContent value="api" className="flex-1 overflow-hidden">
                {apiRows.length === 0 ? (
                  <EmptyState message={t('NO_API_ACTIONS_DEFINED')} />
                ) : (
                  <div className="flex h-full flex-col">
                    <ScrollArea className="flex-1">
                      <Table className="text-sm">
                        <TableHeader>
                          {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id} className="hover:bg-transparent">
                              {headerGroup.headers.map((header) => (
                                <TableHead
                                  key={header.id}
                                  className="font-medium text-muted-foreground"
                                >
                                  {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                      )}
                                </TableHead>
                              ))}
                            </TableRow>
                          ))}
                        </TableHeader>

                        <TableBody>
                          {table.getRowModel().rows.map((row) => (
                            <TableRow key={row.id} className="font-normal">
                              {row.getVisibleCells().map((cell) => (
                                <TableCell key={cell.id}>
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <ScrollBar orientation="horizontal" />
                    </ScrollArea>

                    <div className="flex items-center justify-between border-t px-4 py-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {/* <span>Rows per page</span>
                        <select
                          value={table.getState().pagination.pageSize}
                          onChange={(e) => {
                            table.setPageSize(Number(e.target.value));
                          }}
                          className="h-8 rounded-md border bg-background px-2 text-sm outline-none"
                        >
                          {[10, 20, 50, 100].map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select> */}
                      </div>

                      <div className="flex items-center gap-4">
                        <p className="text-sm text-muted-foreground">
                          Page {table.getState().pagination.pageIndex + 1} {t('OF')}{' '}
                          {table.getPageCount()}
                        </p>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => table.setPageIndex(0)}
                            disabled={!table.getCanPreviousPage()}
                            className="rounded-md border px-2 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {'<<'}
                          </button>
                          <button
                            type="button"
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                            className="rounded-md border px-2 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {t('PREVIOUS')}
                          </button>
                          <button
                            type="button"
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                            className="rounded-md border px-2 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {t('NEXT')}
                          </button>
                          <button
                            type="button"
                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                            disabled={!table.getCanNextPage()}
                            className="rounded-md border px-2 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {'>>'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Tutorial Guide Tab */}
              <TabsContent value="tutorial" className="flex-1 overflow-hidden">
                {data.Tutorial ? (
                  <ScrollArea className="h-full">
                    <div className="px-6 py-5">
                      <MarkdownRenderer content={data.Tutorial} />
                    </div>
                  </ScrollArea>
                ) : (
                  <EmptyState message={t('NO_TUTORIAL_GUIDE')} />
                )}
              </TabsContent>
            </Tabs>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
